import { LanguageOutlined } from '@mui/icons-material';
import { Alert, Divider, Grid } from '@mui/material';
import { isSameDay } from 'date-fns';
import { Fragment, useState } from 'react';

import { CopyText } from '../../../components/CopyText';
import { CustomAccordion } from '../../../components/CustomAccordion';
import { type Veranstaltung, type ZoomMeetingInfo } from '../../../dtos';
import { formatDateRange } from '../../../utils/dateFormat';
import { ZoomRetriggerComponent } from './ZoomRetriggerComponent';

type VeranstaltungsVCProps = {
  readonly veranstaltung: Veranstaltung;
};

export const VeranstaltungsVCComponent = ({ veranstaltung }: VeranstaltungsVCProps) => {
  const [zoomMeetingsToDisplay, setZoomMeetingsToDisplay] = useState(
    getMeetingsOrderedByStartTime(veranstaltung.virtualclassroom?.details ?? []),
  );

  const findReferentenNameForSapId = (sapId: string | number) => {
    const referent = veranstaltung.experteBlockungen.find((blockung) => String(blockung.experteId) === String(sapId));
    const name = referent?.experte ? `${referent.experte.vorname} ${referent.experte.nachname}` : 'Unbekannt';
    return `${name} (${sapId})`;
  };

  const updateZoomMeetingToDisplay = (newZoomMeeting: ZoomMeetingInfo) => {
    const otherMeetings = zoomMeetingsToDisplay.filter((item) => !isSameDay(newZoomMeeting.startTime, item.startTime));
    setZoomMeetingsToDisplay(getMeetingsOrderedByStartTime([...otherMeetings, newZoomMeeting]));
  };

  return (
    <CustomAccordion title="Virtual Classroom" icon={LanguageOutlined} open={veranstaltung.virtualclassroom !== null}>
      {veranstaltung.virtualclassroom ? (
        <Grid container spacing={1} rowSpacing={-5} alignItems="center">
          <Grid size={5}>Typ:</Grid>
          <Grid size={7}>
            <b>{veranstaltung.virtualclassroom.type}</b>
          </Grid>
          <Grid size={12}>
            <Divider sx={{ mt: 1, mb: 1 }} />
          </Grid>
          {zoomMeetingsToDisplay.map((meeting) => {
            console.log(meeting);
            return (
              <Fragment key={meeting.id}>
                <Grid size={5}>Meeting-ID:</Grid>
                <Grid size={7}>
                  <b>{meeting.id}</b>
                </Grid>
                <Grid size={5}>Start/End: </Grid>
                <Grid size={7}>
                  <b>{formatDateRange(new Date(meeting.startTime), new Date(meeting.endTime))}</b>
                </Grid>
                <Grid size={5}>Meeting-URL:</Grid>
                <Grid size={7}>
                  <CopyText text={meeting.joinUrl} />
                </Grid>
                <Grid size={5}>Meeting-Password:</Grid>
                <Grid size={7}>
                  <CopyText text={meeting.password} />
                </Grid>
                <Grid size={12}>Haupt-Trainer:</Grid>
                <Grid size={1}>&nbsp;</Grid>
                <Grid size={4}>{findReferentenNameForSapId(meeting.host.sapId)}</Grid>
                <Grid size={7}>
                  <CopyText text={meeting.startUrl} />
                </Grid>
                <Grid size={12}>Co-Trainer:</Grid>
                {meeting.alternativeHosts.map((host) => (
                  <>
                    <Grid size={1}>&nbsp;</Grid>
                    <Grid size={4}>{findReferentenNameForSapId(host.sapId)}</Grid>
                    <Grid size={7}>
                      <CopyText text={host.joinUrl} />
                    </Grid>
                  </>
                ))}
                <Grid size={12}>
                  <Divider sx={{ mt: 1, mb: 1 }} />
                </Grid>
              </Fragment>
            );
          })}
        </Grid>
      ) : (
        <Alert severity="info">Keine VC-Daten vorhanden</Alert>
      )}
      {veranstaltung.virtualclassroom?.type === 'zoom' && (
        <div style={{ marginTop: 2 }}>
          <ZoomRetriggerComponent veranstaltung={veranstaltung} retriggerSucceeded={updateZoomMeetingToDisplay} />
        </div>
      )}
    </CustomAccordion>
  );
};

function getMeetingsOrderedByStartTime(meetings: ZoomMeetingInfo[]): ZoomMeetingInfo[] {
  return [...meetings].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
}
